import React, { useEffect } from 'react';
import {Box, Button, Divider, Link, Typography} from '@material-ui/core';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {useTranslation} from 'react-i18next';
import {AppointmentDetailsForPrescriptionDto} from '../../../types/appointments';
import {Loading} from '../../index';
import {useAppDispatch, useAppSelector} from '../../../hooks/customReduxHooks';
import {AlertContext} from '../../../contexts/AlertContext';
import {
    applyForPatientDataAccess,
    authorizePatientDataAccess,
    errorTextSpecified,
    fetchPatientDataAccessStatus,
    fetchPrescriptionHistory,
    selectApplicationDateTime,
    selectAuthorizationDateTime,
    selectCodeSent,
    selectErrorText,
    selectPrescriptionHistory,
    selectStatus,
} from '../../../store/prescriptionHistorySlice';
import AccessCodeInput from '../AccessCodeInput/AccessCodeInput';
import {isFulfilled} from '@reduxjs/toolkit';
import PrescriptionHistoryTable from '../PrescriptionHistoryTable/PrescriptionHistoryTable';
import {useContext} from 'react';

const NO_IKP_DATA_ERROR = 'Brak danych.';
const WRONG_CODE_ERROR = 'System zidentyfikował błąd podczas autoryzacji wniosku';

interface PrescriptionHistoryProps {
    appointmentDetails: AppointmentDetailsForPrescriptionDto;
}

const PrescriptionHistory = ({
    appointmentDetails,
}: PrescriptionHistoryProps) => {
    const {t} = useTranslation(i18nNamespaces.PRESCRIPTIONS);
    const {t: errorT} = useTranslation(i18nNamespaces.ERRORS);
    const dispatch = useAppDispatch();
    const openAlert = useContext(AlertContext);
    const status = useAppSelector(selectStatus);
    const codeSent = useAppSelector(selectCodeSent);
    const loading = status === 'loading';
    const errorText = useAppSelector(selectErrorText);
    const prescriptionHistory = useAppSelector(selectPrescriptionHistory);
    const applicationDateTime = useAppSelector(selectApplicationDateTime);
    const authorizationDateTime = useAppSelector(selectAuthorizationDateTime);

    const isAuthorizationActive =
        authorizationDateTime !== null &&
        new Date(authorizationDateTime).getTime() >= Date.now();
    const showSendCodeLink = !isAuthorizationActive;
    const showAccessCodeInput =
        !isAuthorizationActive && (applicationDateTime !== null || codeSent);

    useEffect(() => {
        const checkPatientDataAccessStatus = async () => {
            const statusAction = await dispatch(
                fetchPatientDataAccessStatus(appointmentDetails),
            );

            if (!isFulfilled(statusAction)) return;

            const statusAuthorizationDateTime =
            statusAction.payload?.result?.authorizationDateTime;

            fetchHistory(statusAuthorizationDateTime);
        };


        checkPatientDataAccessStatus();
    }, [dispatch, appointmentDetails]);

    const fetchHistory = (authorizationDateTime: string | null) => {
        if (
            authorizationDateTime !== null &&
            new Date(authorizationDateTime).getTime() >= Date.now()
        ) {
            dispatch(fetchPrescriptionHistory(appointmentDetails));
        }
    }

    const handleUseCode = async () => {
        dispatch(errorTextSpecified(''));
        
        const authorize = await dispatch(
            authorizePatientDataAccess(appointmentDetails),
        );
        if (!isFulfilled(authorize)) return;

        if (!authorize.payload?.isSuccess) {
            if(authorize.payload?.message === WRONG_CODE_ERROR) 
                dispatch(errorTextSpecified(errorT('patientDataAccessWrongCodeError')));
            else
                openAlert({
                    severity: 'error',
                    message: errorT('patientDataAccessUseCodeError'),
                });
            return;
        }

        const authorizationDateTime =
            authorize.payload?.result?.authorizationDateTime;

        fetchHistory(authorizationDateTime);
    };

    const handleSendCode = async () => {
        const apply = await dispatch(applyForPatientDataAccess(appointmentDetails));

        if (!isFulfilled(apply)) return;

        if (!apply.payload?.isSuccess) {
            openAlert({
                severity: 'error',
                message:
                    apply.payload?.message === NO_IKP_DATA_ERROR
                        ? errorT('patientDataAccessNoChannelError')
                        : errorT('patientDataAccessSendCodeError'),
            });
            return;
        }

        dispatch(fetchPatientDataAccessStatus(appointmentDetails));
    };

    return (
        <>
            <Loading loading={loading} withBackdrop />
            <h2>{t('accessAuthorization')}</h2>
            {showSendCodeLink && (
                <Box style={{marginBottom: 30}}>
                    <Link
                        style={{cursor: 'pointer'}}
                        color={'secondary'}
                        onClick={handleSendCode}
                    >
                        {t('sendCode')}
                    </Link>
                </Box>
            )}
            {showAccessCodeInput && (
                <AccessCodeInput
                    onSubmit={handleUseCode}
                    disabled={loading}
                    errorText={errorText}
                />
            )}
            {isAuthorizationActive && (
                <Typography
                    variant={'body1'}
                    style={{marginBottom: 10}}
                    >
                        {t('authorizationActiveTo')}: {new Date(authorizationDateTime!).toLocaleString('pl-PL')}
                </Typography>
            )}
            <Divider style={{height: 1, margin: '30px 0'}} />
            {prescriptionHistory !== null &&
                (prescriptionHistory?.length > 0 ? (
                    <PrescriptionHistoryTable
                        appointmentDetails={appointmentDetails}
                    />
                ) : (
                    <Typography
                        variant={'h5'}
                        style={{textAlign: 'center', marginBottom: 10}}
                    >
                        {t('thereAreNoPrescriptionsInHistory')}
                    </Typography>
                ))}
        </>
    );
};

export default PrescriptionHistory;
